import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

interface ErrorConfig {
  code: number;
  badge: string;
  title: string;
  description: string;
  accent: string;
  accentSubtle: string;
  accentBorder: string;
  primaryLabel: string;
  primaryHref?: string;
  primaryAction?: () => void;
  secondaryLabel: string;
  secondaryHref?: string;
  icon: React.ReactNode;
}

const icons: Record<number, React.ReactNode> = {
  400: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 10v7M16 21h.01" strokeWidth="2.5"/>
      <path d="M13.27 5.5l-9.5 16.5A3 3 0 006.36 27h19.28a3 3 0 002.59-5L18.73 5.5a3 3 0 00-5.46 0z"/>
    </svg>
  ),
  401: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="14" width="20" height="14" rx="3"/>
      <path d="M11 14v-4a5 5 0 0110 0v4"/>
      <circle cx="16" cy="21" r="2" fill="currentColor" stroke="none"/>
    </svg>
  ),
  403: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="16" cy="16" r="11"/>
      <path d="M10 22L22 10"/>
    </svg>
  ),
  404: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="16" cy="16" r="11"/>
      <path d="M16 12v5M16 21h.01" strokeWidth="2.5"/>
    </svg>
  ),
  500: (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="5" y="7" width="22" height="18" rx="3"/>
      <path d="M9 12h2M9 16h2M13 12h10M13 16h6"/>
    </svg>
  ),
};

const configs: Record<number, Omit<ErrorConfig, 'code' | 'icon' | 'primaryAction'>> = {
  400: {
    badge: "Requête invalide",
    title: "La requête ne peut pas être traitée",
    description: "Le serveur n'a pas pu comprendre la requête : syntaxe incorrecte ou paramètres manquants. Vérifiez l'URL et les données envoyées.",
    accent: '#F97316',
    accentSubtle: 'rgba(249,115,22,0.1)',
    accentBorder: 'rgba(249,115,22,0.25)',
    primaryLabel: "← Accueil",
    primaryHref: "/",
    secondaryLabel: "← Retour",
  },
  401: {
    badge: "Non authentifié",
    title: "Identification requise",
    description: "Vous devez vous authentifier pour accéder à cette ressource. Connectez-vous avec votre compte Colibri Technologies.",
    accent: '#7C3AED',
    accentSubtle: 'rgba(124,58,237,0.1)',
    accentBorder: 'rgba(124,58,237,0.25)',
    primaryLabel: "← Se connecter",
    primaryHref: "/login",
    secondaryLabel: "← Accueil",
    secondaryHref: "/",
  },
  403: {
    badge: "Accès interdit",
    title: "Vous n'êtes pas autorisé",
    description: "Vous n'avez pas les permissions nécessaires pour accéder à cette page. Contactez-nous si vous pensez qu'il s'agit d'une erreur.",
    accent: '#E879F9',
    accentSubtle: 'rgba(232,121,249,0.1)',
    accentBorder: 'rgba(232,121,249,0.25)',
    primaryLabel: "← Accueil",
    primaryHref: "/",
    secondaryLabel: "← Support",
    secondaryHref: "mailto:contact@colibritechnologies.ca",
  },
  404: {
    badge: "Page introuvable",
    title: "Cette page n'existe pas",
    description: "La page que vous recherchez a peut-être été déplacée, supprimée ou l'URL est incorrecte.",
    accent: '#10B981',
    accentSubtle: 'rgba(16,185,129,0.1)',
    accentBorder: 'rgba(16,185,129,0.25)',
    primaryLabel: "← Accueil",
    primaryHref: "/",
    secondaryLabel: "← Retour",
  },
  500: {
    badge: "Erreur serveur",
    title: "Une erreur s'est produite",
    description: "Le serveur a rencontré une erreur interne. Nos équipes en sont informées. Veuillez réessayer dans quelques instants.",
    accent: '#00BCD4',
    accentSubtle: 'rgba(0,188,212,0.1)',
    accentBorder: 'rgba(0,188,212,0.25)',
    primaryLabel: "← Réessayer",
    secondaryLabel: "← Accueil",
    secondaryHref: "/",
  },
};

interface ErrorPageProps {
  code?: number;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ code = 404 }) => {
  const navigate = useNavigate();
  const cfg = configs[code] ?? configs[404];
  const icon = icons[code] ?? icons[404];

  const handlePrimary = () => {
    if (cfg.primaryHref) return;
    if (code === 500) window.location.reload();
    else navigate(-1);
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        background: 'var(--color-bg)',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-body)',
        textAlign: 'center',
      }}
    >
      <div style={{ width: '100%', maxWidth: '520px' }}>

        {/* Logo */}
        <Link to="/" style={{ display: 'inline-block', textDecoration: 'none', marginBottom: '40px' }}>
          <img src="/logo.svg" width="56" height="56" alt="Colibri Technologies" />
        </Link>

        {/* Card */}
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: '1px solid var(--color-border)',
          borderRadius: '24px',
          padding: '48px 40px',
          boxShadow: '0 8px 48px rgba(0,0,0,0.5)',
        }}>
          {/* Icon circle */}
          <div style={{
            width: '72px', height: '72px',
            margin: '0 auto 24px',
            borderRadius: '50%',
            background: cfg.accentSubtle,
            border: `1px solid ${cfg.accentBorder}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: cfg.accent,
          }}>
            {icon}
          </div>

          {/* Error code */}
          <div style={{
            fontWeight: 800,
            fontSize: 'clamp(4rem,16vw,6rem)',
            lineHeight: 1,
            letterSpacing: '-0.04em',
            marginBottom: '6px',
            color: cfg.accent,
          }}>
            {code}
          </div>

          {/* Badge */}
          <div style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            background: cfg.accentSubtle,
            color: cfg.accent,
            border: `1px solid ${cfg.accentBorder}`,
            marginBottom: '20px',
          }}>
            {cfg.badge}
          </div>

          {/* Title */}
          <h1 style={{
            fontWeight: 700,
            fontSize: 'clamp(1.2rem,3vw,1.5rem)',
            color: 'var(--color-text-primary)',
            marginBottom: '12px',
            lineHeight: 1.25,
            fontFamily: 'var(--font-display)',
          }}>
            {cfg.title}
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '0.9375rem',
            lineHeight: 1.65,
            color: 'var(--color-text-secondary)',
            marginBottom: '36px',
          }}>
            {cfg.description}
          </p>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {cfg.primaryHref ? (
              cfg.primaryHref.startsWith('mailto') || cfg.primaryHref.startsWith('http') ? (
                <a href={cfg.primaryHref} style={primaryStyle}>{cfg.primaryLabel}</a>
              ) : (
                <Link to={cfg.primaryHref} style={primaryStyle}>{cfg.primaryLabel}</Link>
              )
            ) : (
              <button onClick={handlePrimary} style={{ ...primaryStyle, cursor: 'pointer', border: 'none' }}>
                {cfg.primaryLabel}
              </button>
            )}

            {cfg.secondaryHref ? (
              cfg.secondaryHref.startsWith('mailto') || cfg.secondaryHref.startsWith('http') ? (
                <a href={cfg.secondaryHref} style={ghostStyle}>{cfg.secondaryLabel}</a>
              ) : (
                <Link to={cfg.secondaryHref} style={ghostStyle}>{cfg.secondaryLabel}</Link>
              )
            ) : (
              <button onClick={() => navigate(-1)} style={{ ...ghostStyle, cursor: 'pointer', border: '1px solid var(--color-border)' }}>
                {cfg.secondaryLabel}
              </button>
            )}
          </div>

          {/* Divider */}
          <div style={{
            width: '48px', height: '2px',
            background: cfg.accent,
            borderRadius: '2px',
            margin: '24px auto 0',
            opacity: 0.5,
          }} />
        </div>
      </div>
    </div>
  );
};

const primaryStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '0.875rem',
  fontWeight: 600,
  textDecoration: 'none',
  background: 'var(--gradient-primary)',
  color: '#0A0A0F',
  fontFamily: 'var(--font-body)',
};

const ghostStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  borderRadius: '12px',
  fontSize: '0.875rem',
  fontWeight: 500,
  textDecoration: 'none',
  background: 'rgba(255,255,255,0.04)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border)',
  fontFamily: 'var(--font-body)',
};

export default ErrorPage;
